import * as React from 'react'
import { FlatList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Select from '@components/select/Select'
import Modal from '@components/modal/Modal'
import Drawer from '@components/drawer/Drawer'
import Icon from '@components/icon/Icons'
import TextInput from '@components/textInput/TextInput'
import Button from '@components/button/Button'
import SmallButton from '@components/button/SmallButton'

import sanitize from '@utils/Sanitizer'
import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import { useFormik } from 'formik'
import * as yup from 'yup'

interface Option {
  value: ContactEtatDesLieux
  label: string
}

interface Form {
  nom?: string
  prenom?: string
  email?: string
}

interface Props {
  children: React.ReactNode
  title: string
  subtitle?: string
  disabled?: boolean
  data: Option[]
  value: ContactEtatDesLieux[]
  onItemSelected: (item: ContactEtatDesLieux[]) => void
  sectionTitle?: string
}

const INIT_FORM: Form = { prenom: '', nom: '', email: '' }

const validationSchema = yup.object().shape({
  email: yup.string().email('errors.form.email').required('errors.form.empty'),
})

const SearchModal = ({ data, onItemSelected, value, subtitle, title, sectionTitle }: Omit<Props, 'children'>) => {
  const i18n = useI18n()

  const [search, setSearch] = React.useState('')
  const [selected, setSelected] = React.useState(value)
  const [, bp] = useBreakpoint()

  const [newContacts, setNewContacts] = React.useState<ContactEtatDesLieux[]>([])

  const items = React.useMemo(
    () =>
      [...data, ...newContacts.map((c) => ({ label: `${c.prenom} ${c.nom} (${c.email})`, value: c }))].filter((d) =>
        sanitize(d.label).includes(sanitize(search))
      ),
    [data, search, newContacts]
  )

  const { values, setFieldValue, handleSubmit, dirty, resetForm, isValid, errors } = useFormik<Form>({
    enableReinitialize: true,
    initialValues: INIT_FORM,
    onSubmit: () => {
      setSelected([...selected, values])
      setNewContacts([...newContacts, values])
      resetForm()
    },
    validationSchema,
  })

  const onSubmit = () => {
    onItemSelected(selected || '')
    Modal.close()
  }

  const onSelected = (val: ContactEtatDesLieux) => {
    const active = selected.some((v) => v.email === val.email)

    if (active) {
      setSelected(selected.filter((s) => s !== val))
    } else {
      setSelected([...selected, val])
    }
  }

  const renderSectionHeader = () => (
    <Section>
      <SectionTitle>{sectionTitle}</SectionTitle>
    </Section>
  )

  const renderItem = ({ item }: { item: Option }) => {
    const focused = selected.includes(item.value)

    return (
      <SelectContainer key={`${item.value.prenom}-${item.value.nom}-${item.value.email}`}>
        <Select
          label={item.label}
          onClick={() => onSelected(item.value)}
          focused={focused}
          selectStyle={!focused ? 'empty' : 'default'}
        />
      </SelectContainer>
    )
  }

  return (
    <SearchContainer>
      <InfoContainer>
        <ModalTitle>{title}</ModalTitle>
        {!!subtitle && <Current>{subtitle}</Current>}

        <Close activeOpacity={0.8} onPress={() => Modal.close()}>
          <Icon name="close" size={24} />
        </Close>
      </InfoContainer>
      <ListContainer>
        <InputContainer>
          <TextInput
            value={search}
            onChange={setSearch}
            placeholder={i18n.t('common.search')}
            left={{ name: 'search' }}
          />
        </InputContainer>

        <FlatList
          key={bp}
          data={items}
          renderItem={renderItem}
          keyExtractor={(_, idx) => `${idx}`}
          extraData={selected}
          ListHeaderComponent={renderSectionHeader()}
          ItemSeparatorComponent={ModalSeparator}
          numColumns={bp === 'phone' ? 1 : 2}
          ListEmptyComponent={<NoContact>{i18n.t('screens.edlSignature.contacts.noOption')}</NoContact>}
        />
      </ListContainer>

      <FormContainer>
        <Row>
          <TextInputContainer>
            <TextInput
              value={values.prenom}
              onChange={(value) => setFieldValue('prenom', value)}
              placeholder={i18n.t('screens.edlSignature.contacts.prenom')}
            />
          </TextInputContainer>
          <TextInputContainer>
            <TextInput
              value={values.nom}
              onChange={(value) => setFieldValue('nom', value)}
              placeholder={i18n.t('screens.edlSignature.contacts.nom')}
            />
          </TextInputContainer>
          <TextInputContainer>
            <TextInput
              value={values.email}
              onChange={(value) => setFieldValue('email', value)}
              placeholder={i18n.t('screens.edlSignature.contacts.email')}
              error={errors.email ? i18n.t(errors.email) : undefined}
            />
          </TextInputContainer>
          <SmallButtonContainer>
            <SmallButton
              status={dirty && isValid ? 'active' : 'disabled'}
              onClick={handleSubmit}
              label={i18n.t('common.add')}
            />
          </SmallButtonContainer>
        </Row>
      </FormContainer>

      <InfoContainer>
        <ButtonContainer>
          <Button label={i18n.t('common.validate')} status={!!selected ? 'active' : 'disabled'} onClick={onSubmit} />
        </ButtonContainer>
      </InfoContainer>
    </SearchContainer>
  )
}

const SearchDrawer = ({ data, onItemSelected, value, title, sectionTitle }: Omit<Props, 'children'>) => {
  const i18n = useI18n()

  const [selected, setSelected] = React.useState(value)
  const [search, setSearch] = React.useState('')
  const [displayForm, setDisplayForm] = React.useState(false)

  const [newContacts, setNewContacts] = React.useState<ContactEtatDesLieux[]>([])

  const items = React.useMemo(
    () =>
      [...data, ...newContacts.map((c) => ({ label: `${c.prenom} ${c.nom} (${c.email})`, value: c }))].filter((d) =>
        sanitize(d.label).includes(sanitize(search))
      ),
    [data, search, newContacts]
  )

  const { values, setFieldValue, handleSubmit, dirty, resetForm, isValid, errors } = useFormik<Form>({
    enableReinitialize: true,
    initialValues: INIT_FORM,
    onSubmit: () => {
      setSelected([...selected, values])
      setNewContacts([...newContacts, values])
      resetForm()
      setDisplayForm(false)
    },
    validationSchema,
  })

  const onSelected = (val: ContactEtatDesLieux) => {
    const active = selected.some((v) => v.email === val.email)

    if (active) {
      setSelected(selected.filter((s) => s !== val))
    } else {
      setSelected([...selected, val])
    }
  }

  const renderItem = ({ item }: { item: Option }) => {
    const focused = selected.includes(item.value)

    return (
      <Cell onPress={() => onSelected(item.value)} activeOpacity={0.6}>
        <Item>{item.label}</Item>
        {focused && <Icon name="check" color={theme.colors.primary} size={24} />}
      </Cell>
    )
  }

  const validate = () => {
    onItemSelected(selected)
    Drawer.close()
  }

  return (
    <MobileContainer>
      <Header>
        <SmallHeader onPress={() => (displayForm ? setDisplayForm(false) : Drawer.close())}>
          <Icon name="close" size={35} color={theme.colors.primary} />
        </SmallHeader>
        <CenterHeader>
          <DrawerTitle>{title}</DrawerTitle>
        </CenterHeader>
        <SmallHeader />
      </Header>
      {displayForm ? (
        <>
          <FormContainer>
            <TextInputContainer>
              <TextInput
                value={values.prenom}
                onChange={(value) => setFieldValue('prenom', value)}
                placeholder={i18n.t('screens.edlSignature.contacts.prenom')}
              />
            </TextInputContainer>
            <TextInputContainer>
              <TextInput
                value={values.nom}
                onChange={(value) => setFieldValue('nom', value)}
                placeholder={i18n.t('screens.edlSignature.contacts.nom')}
              />
            </TextInputContainer>
            <TextInputContainer>
              <TextInput
                value={values.email}
                onChange={(value) => setFieldValue('email', value)}
                placeholder={i18n.t('screens.edlSignature.contacts.email')}
                error={errors.email ? i18n.t(errors.email) : undefined}
              />
            </TextInputContainer>
          </FormContainer>

          <Footer>
            <Button
              status={dirty && isValid ? 'active' : 'disabled'}
              onClick={handleSubmit}
              label={i18n.t('common.add')}
            />
          </Footer>
        </>
      ) : (
        <>
          <TopContainer>
            <TextInput
              onChange={setSearch}
              value={search}
              searchStyle="onLight"
              left={{ name: 'search' }}
              placeholder={i18n.t('screens.edl.spaces.placeholder')}
            />
          </TopContainer>

          <SmallButtonContainer>
            <View>
              <SmallButton status="active" onClick={() => setDisplayForm(true)} label={i18n.t('common.add')} />
            </View>
          </SmallButtonContainer>

          <FlatList
            data={items}
            renderItem={renderItem}
            ItemSeparatorComponent={DrawerSeparator}
            keyExtractor={(_, idx) => `${idx}`}
            ListEmptyComponent={<NoContact>{i18n.t('screens.edlSignature.contacts.noOption')}</NoContact>}
          />

          <Footer>
            <Button label={i18n.t('common.validate')} onClick={validate} status={!!selected ? 'active' : 'disabled'} />
          </Footer>
        </>
      )}
    </MobileContainer>
  )
}

const ModalList = ({ children, disabled, ...rest }: Props) => {
  const [, bp] = useBreakpoint()

  const openSearch = () => {
    if (bp !== 'phone') {
      Modal.open({
        Content: () => <SearchModal {...rest} />,
      })
    } else {
      Drawer.open({
        render: () => <SearchDrawer {...rest} />,
        hideTarget: true,
      })
    }
  }

  return (
    <TouchableOpacity activeOpacity={0.9} onPress={openSearch} disabled={disabled}>
      {children}
    </TouchableOpacity>
  )
}

const ModalListService = {
  open: (props: Omit<Props, 'children'>, phone?: boolean) => {
    if (!phone) {
      Modal.open({
        Content: () => <SearchModal {...props} />,
      })
    } else {
      Drawer.open({
        render: () => <SearchDrawer {...props} />,
        hideTarget: true,
      })
    }
  },
  close: Modal.close,
}

export { ModalListService }
export default ModalList

//MODAL
const SearchContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'width: 880px'};
`

const InfoContainer = styled(View)`
  padding: 24px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  position: relative;
`

const ModalTitle = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 24px;
  margin-bottom: 8px;
`

const Current = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
`

const Close = styled(TouchableOpacity)`
  position: absolute;
  top: ${(props) => (props.theme.breakpoint === 'phone' ? 4 : 24)}px;
  right: ${(props) => (props.theme.breakpoint === 'phone' ? 4 : 24)}px;
  padding: 5px;
`

const ListContainer = styled(View)`
  padding: 24px;
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 325 : 400)}px;
  background-color: ${(props) => props.theme.colors.background};
`

const InputContainer = styled(View)`
  margin-bottom: 24px;
`

const SelectContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 400px;`}
  margin-right: 24px;
`

const ModalSeparator = styled(View)`
  height: 24px;
`

const ButtonContainer = styled(View)`
  align-self: center;
  width: 275px;
`

//DRAWER
const TopContainer = styled(View)`
  padding: 0 21px 16px;
`

const Cell = styled(TouchableOpacity)`
  background-color: ${(props) => props.theme.colors.background};
  padding: 0 16px;
  height: 45px;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`

const Item = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const DrawerSeparator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const Footer = styled(View)`
  margin: 16px;
`

const Header = styled(View)`
  flex-direction: row;
  padding: 21px;
`

const SmallHeader = styled(TouchableOpacity)`
  flex: 1;
`

const CenterHeader = styled(View)`
  flex: 4;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const DrawerTitle = styled(Text)<{ top?: number }>`
  ${(props) => props.theme.fonts.Headline};
  margin-top: ${(props) => (props.top ? props.top : 0)}px;
`

const MobileContainer = styled(View)`
  flex: 1;
  padding-bottom: ${(props) => props.theme.insets.bottom}px;
`

const Section = styled(View)`
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.backgroundSecondary : 'transparent'};
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  padding: 15px 16px;

  ${(props) => props.theme.breakpoint !== 'phone' && 'align-items: center;'}
`

const SectionTitle = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex: 1;`}
`

const FormContainer = styled(View)`
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `flex:1;`
      : `
      padding: 24px;
      background-color: ${props.theme.colors.backgroundTertiary};
      position: relative;`}
`

const Row = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
`

const TextInputContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin-bottom: 12px; padding: 0px 24px;` : `width: 240px;`)}
`

const SmallButtonContainer = styled(View)`
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
      flex-direction: row;
      justify-content: flex-end;
      padding-right: 24px;
      margin-bottom: 12px;`
      : `margin-top: 12px;`}
`

const NoContact = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
